import React, { useCallback, useState } from 'react';
import { Box, Button, Input, Stack, Tooltip } from '@chakra-ui/react';
import uniqueId from 'lodash/uniqueId';
import { useEditor } from 'components/Canvas';
import useTemplateEditorContext from 'scenes/templateEditor/hooks/useTemplateEditorContext';
import PanelTitle from './components/PanelTitle';
import Icons from '../../Icons';
import { loadFont } from 'helpers/editor';
import {
  SUPPLIED_VALUE_PLACEHOLDER,
  toIso8601,
  validateSuppliedValueConfig,
  sampleExpiryDate,
} from 'helpers/suppliedValue';

const labelStyle = {
  fontSize: '14px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};

const inputStyle = { backgroundColor: 'white', color: 'black' };

const errorStyle = { fontSize: '12px', color: '#feb2b2' };

const hintStyle = { fontSize: '12px', color: 'rgba(255,255,255,0.6)' };

function PromoCode() {
  const editor = useEditor();
  const { defaultFont } = useTemplateEditorContext();
  const [groupId, setGroupId] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [ttlDays, setTtlDays] = useState('');
  const [displayValue, setDisplayValue] = useState('');
  const [exportKey, setExportKey] = useState('');

  const errors = validateSuppliedValueConfig({
    company_group_id: groupId,
    start,
    end,
    ttl_days: ttlDays,
    display_value: displayValue,
    export_key: exportKey,
  });

  // the var itself no longer renders a date, so show what the consuming var
  // would print. Counted from today, as the server's preview is.
  const expirySample = sampleExpiryDate(ttlDays);

  const addObjectToCanvas = useCallback(async () => {
    await loadFont(defaultFont);
    let __fontFilename;

    if ((defaultFont?.url || '').match(/s3.amazonaws.com/g)) {
      __fontFilename = (defaultFont?.url || '')
        .substring(defaultFont.url.lastIndexOf('/') + 1)
        .split('?')[0];
    } else {
      __fontFilename = (defaultFont?.url || '').substring(
        (defaultFont?.url || '').lastIndexOf('/') + 1
      );
    }

    // start of day for the lower bound, end of day for the upper, so an
    // inclusive date range in the UI matches the backend's datetime window.
    const startIso = toIso8601(start, 'start');
    const endIso = toIso8601(end, 'end');
    const ttl = ttlDays.trim() === '' ? undefined : parseInt(ttlDays, 10);
    const display = displayValue.trim();
    const exported = exportKey.trim();

    const objectOptions = {
      type: 'SuppliedValue',
      width: 240,
      fontSize: 64,
      // no text is passed: the object derives it from the metadata below, so
      // the canvas shows the sample code and expiry exactly as the preview
      // will, from one source of truth.
      fontFamily: defaultFont?.name || '',
      textAlign: 'center',
      fill: '#000000',
      metadata: {
        company_group_id: groupId.trim(),
        // the optional config is only set when provided, so an unused bound
        // stays absent from metadata rather than being stored as null.
        ...(startIso && { start: startIso }),
        ...(endIso && { end: endIso }),
        ...(ttl && { ttl_days: ttl }),
        ...(display && { display_value: display }),
        ...(exported && { export_key: exported }),
        __fontFilename: __fontFilename,

        // unique id to allow us to tie back a newly saved record with it's entity id
        __session_var_id: uniqueId('s'),
      },
    };
    editor.add(objectOptions);
  }, [
    editor,
    defaultFont,
    groupId,
    start,
    end,
    ttlDays,
    displayValue,
    exportKey,
  ]);

  return (
    <Box sx={{ flex: 1, marginTop: '1.5rem' }}>
      <PanelTitle title="Click to add to canvas" subMenu="PromoCodeTutorial" />
      <Stack spacing={3} sx={{ color: '#ffffff', padding: '1.5rem' }}>
        <Box sx={labelStyle}>
          <Box>Code group</Box>
          <Tooltip label="The group of codes to draw from. A code is allocated from this group when the ad is produced, and the same order always keeps the code it was first given.">
            <Box color="rgba(255,255,255,0.6)" cursor="pointer">
              <Icons.Info size={22} />
            </Box>
          </Tooltip>
        </Box>
        <Input
          name="promo-code-group"
          fontSize="14px"
          borderRadius={3}
          style={inputStyle}
          value={groupId}
          placeholder="Code group"
          onChange={e => setGroupId(e.target.value)}
        />

        <Box sx={labelStyle}>
          <Box>Sample code</Box>
          <Tooltip label="Optional. A representative code shown on the canvas and in previews so you can check how a real one fits. It is never given to a customer. Leave blank to show a generic placeholder.">
            <Box color="rgba(255,255,255,0.6)" cursor="pointer">
              <Icons.Info size={22} />
            </Box>
          </Tooltip>
        </Box>
        <Input
          name="promo-code-display-value"
          fontSize="14px"
          borderRadius={3}
          style={inputStyle}
          value={displayValue}
          placeholder={SUPPLIED_VALUE_PLACEHOLDER}
          onChange={e => setDisplayValue(e.target.value)}
        />

        <Box sx={labelStyle}>
          <Box>Start date</Box>
          <Tooltip label="Optional. Codes are only allocated on or after this date. Leave blank for no start date.">
            <Box color="rgba(255,255,255,0.6)" cursor="pointer">
              <Icons.Info size={22} />
            </Box>
          </Tooltip>
        </Box>
        <Input
          name="promo-code-start"
          type="date"
          fontSize="14px"
          borderRadius={3}
          style={inputStyle}
          value={start}
          onChange={e => setStart(e.target.value)}
        />

        <Box sx={labelStyle}>
          <Box>End date</Box>
          <Tooltip label="Optional. Codes stop being allocated after this date. Orders that already have a code keep showing it. Leave blank for no end date.">
            <Box color="rgba(255,255,255,0.6)" cursor="pointer">
              <Icons.Info size={22} />
            </Box>
          </Tooltip>
        </Box>
        <Input
          name="promo-code-end"
          type="date"
          fontSize="14px"
          borderRadius={3}
          style={inputStyle}
          value={end}
          onChange={e => setEnd(e.target.value)}
        />
        {errors.end && <Box sx={errorStyle}>{errors.end}</Box>}

        <Box sx={labelStyle}>
          <Box>Expires after (days)</Box>
          <Tooltip label="Optional. How long the code stays valid, counted from the day it is allocated. On its own this prints nothing — name a field below to show the date. Leave blank if the code never expires.">
            <Box color="rgba(255,255,255,0.6)" cursor="pointer">
              <Icons.Info size={22} />
            </Box>
          </Tooltip>
        </Box>
        <Input
          name="promo-code-ttl"
          fontSize="14px"
          borderRadius={3}
          style={inputStyle}
          value={ttlDays}
          placeholder="Never expires"
          onChange={e => setTtlDays(e.target.value)}
        />
        {errors.ttl_days && <Box sx={errorStyle}>{errors.ttl_days}</Box>}
        {!errors.ttl_days && expirySample && (
          <Box sx={hintStyle}>
            A code issued today would expire {expirySample}.
          </Box>
        )}

        <Box sx={labelStyle}>
          <Box>Expiry date field name</Box>
          <Tooltip label="Optional. Names a field that carries the expiry date, so you can place and word it yourself. Enter this same name in a text field's {{ }} expression to show the date there. Requires an expiry above.">
            <Box color="rgba(255,255,255,0.6)" cursor="pointer">
              <Icons.Info size={22} />
            </Box>
          </Tooltip>
        </Box>
        <Input
          name="promo-code-export-key"
          fontSize="14px"
          borderRadius={3}
          style={inputStyle}
          value={exportKey}
          placeholder="promo_exp"
          onChange={e => setExportKey(e.target.value)}
        />
        {errors.export_key ? (
          <Box sx={errorStyle}>{errors.export_key}</Box>
        ) : (
          <Box sx={hintStyle}>
            {exportKey.trim()
              ? `Add {{${exportKey.trim()}}} to a text field to show the date.`
              : "Enter this same name in a text field's {{ }} expression to show the expiry date."}
          </Box>
        )}

        <Button
          size="sm"
          w="100%"
          disabled={Object.keys(errors).length > 0}
          onClick={() => addObjectToCanvas()}
          sx={{
            backgroundColor: 'rgba(255,255,255,0.1)',
            color: 'rgba(255,255,255,0.9)',
            height: '48px',
            justifyContent: 'center',
            borderRadius: '4px',
            marginTop: '25px !important',
          }}
          _hover={{
            backgroundColor: 'rgba(255,255,255,0.15)',
          }}
          _active={{
            backgroundColor: 'rgba(255,255,255,0.2)',
          }}
        >
          Add Promo Code
        </Button>
      </Stack>
    </Box>
  );
}

export default PromoCode;
